/**
 * Podaci naše firme - jedno mesto za sve što izlazi klijentu pod našim imenom.
 *
 * Do sada ruta (`GET/PUT /api/admin/payments/settings`) jeste postojala, ali je
 * **nijedna stranica nije koristila**: podaci su se upisivali direktno u bazu.
 * Zato je uplatnica umela da bude bez primaoca, a ugovor je izlazio sa praznim
 * potpisnim blokom (TODO PD-3).
 *
 * Polja su grupisana po tome **gde se vide**, a ne po abecedi: to je jedini
 * raspored po kom čovek koji ovo popunjava zna šta će se pokvariti ako preskoči
 * polje. Uz svaku grupu piše gde tačno ta polja izlaze.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Building2, FileSignature, Landmark, RefreshCw, Save } from 'lucide-react';
import { api } from '../../api/client';

type Settings = {
  companyName: string;
  taxId: string;
  registrationNumber: string;
  address: string;
  city: string;
  bankAccount: string;
  bankName: string;
  paymentCode: string;
  directorName: string;
  directorTitle: string;
  contactEmail: string;
  website: string;
};

const PRAZNO: Settings = {
  companyName: '', taxId: '', registrationNumber: '', address: '', city: '',
  bankAccount: '', bankName: '', paymentCode: '221',
  directorName: '', directorTitle: 'Direktor', contactEmail: '', website: '',
};

/**
 * Upozorenja, ne prepreke.
 *
 * PIB od devet i matični broj od osam cifara su pravilo, ali zatečeni red u
 * bazi može biti stariji od pravila - a stranica koja odbija da sačuva podatke
 * koji su već tu ne pomaže nikome. Zato se greška **pokaže**, a čuvanje prolazi.
 */
function primedbe(s: Settings): string[] {
  const out: string[] = [];
  if (s.taxId && !/^\d{9}$/.test(s.taxId.trim())) out.push('PIB obično ima tačno 9 cifara.');
  if (s.registrationNumber && !/^\d{8}$/.test(s.registrationNumber.trim())) {
    out.push('Matični broj obično ima tačno 8 cifara.');
  }
  if (s.contactEmail && !/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(s.contactEmail.trim())) {
    out.push('E-pošta ne izgleda kao ispravna adresa.');
  }
  if (s.paymentCode && !/^\d{2,3}$/.test(s.paymentCode.trim())) {
    out.push('Šifra plaćanja je dvocifrena ili trocifrena (221 = promet robe i usluga).');
  }
  return out;
}

/** Šta nedostaje, i šta zbog toga ne radi kako treba. */
function nedostaci(s: Settings): Array<{ polje: string; posledica: string }> {
  const out: Array<{ polje: string; posledica: string }> = [];
  if (!s.companyName.trim()) out.push({ polje: 'Poslovno ime', posledica: 'ugovor i uplatnica ostaju bez izdavaoca' });
  if (!s.taxId.trim()) out.push({ polje: 'PIB', posledica: 'ugovor izlazi sa prazninom umesto PIB-a' });
  if (!s.registrationNumber.trim()) out.push({ polje: 'Matični broj', posledica: 'isto, u zaglavlju ugovora' });
  if (!s.bankAccount.trim()) out.push({ polje: 'Tekući račun', posledica: 'uplatnica nema na šta da uputi klijenta' });
  if (!s.directorName.trim()) out.push({ polje: 'Potpisnik', posledica: 'potpisni blok ugovora ostaje prazan' });
  if (!s.contactEmail.trim()) out.push({ polje: 'Kontakt e-pošta', posledica: 'politika privatnosti upućuje na kontakt kog nema' });
  return out;
}

export function CompanySettingsPage() {
  const [forma, setForma] = useState<Settings>(PRAZNO);
  const [snimljeno, setSnimljeno] = useState<Settings>(PRAZNO);
  const [ucitavanje, setUcitavanje] = useState(false);
  const [salje, setSalje] = useState(false);
  const [greska, setGreska] = useState<string | null>(null);
  const [poruka, setPoruka] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setUcitavanje(true);
    setGreska(null);
    try {
      const red = await api.get<Partial<Settings> | null>('/admin/payments/settings');
      const vrednosti = { ...PRAZNO, ...(red ?? {}) } as Settings;
      setForma(vrednosti);
      setSnimljeno(vrednosti);
    } catch (e: any) {
      setGreska(e?.message || 'Greška pri učitavanju');
    } finally {
      setUcitavanje(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const izmenjeno = JSON.stringify(forma) !== JSON.stringify(snimljeno);

  const sacuvaj = async () => {
    if (salje) return;
    setSalje(true);
    setGreska(null);
    setPoruka(null);
    try {
      await api.put('/admin/payments/settings', forma);
      setSnimljeno(forma);
      setPoruka('Sačuvano. Novi ugovori i uplatnice koriste ove podatke.');
    } catch (e: any) {
      setGreska(e?.data?.message || e?.message || 'Čuvanje nije uspelo');
    } finally {
      setSalje(false);
    }
  };

  const polja = (k: keyof Settings) => ({
    value: forma[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForma({ ...forma, [k]: e.target.value }),
    style: polje,
  });

  const upozorenja = primedbe(forma);
  const prazna = nedostaci(snimljeno);

  return (
    <div style={{ display: 'grid', gap: 18, maxWidth: 860 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--color-text-strong)' }}>
          Podaci firme
        </h2>
        <button onClick={() => void refresh()} style={btnGhost}><RefreshCw size={13} /> Osveži</button>
        <button
          onClick={() => void sacuvaj()}
          disabled={salje || !izmenjeno}
          style={{
            ...btnPrimary,
            marginLeft: 'auto',
            // Sama providnost se na tamnoj temi jedva vidi, pa je dugme
            // izgledalo aktivno iako nije. Iskljuceno stanje menja i boju.
            background: izmenjeno ? 'var(--color-primary, #2563eb)' : 'var(--color-bg-muted, #334155)',
            color: izmenjeno ? '#fff' : 'var(--color-text-muted, #94a3b8)',
            cursor: izmenjeno && !salje ? 'pointer' : 'not-allowed',
          }}
        >
          <Save size={14} /> {salje ? 'Čuvam…' : 'Sačuvaj'}
        </button>
      </header>

      <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>
        Ovo su podaci <strong>naše firme</strong>, ne klijenta. Izlaze na tri mesta: uplatnica koju
        klijent dobija uz račun, ugovor koji potpisuje, i politika privatnosti u aplikaciji.
      </p>

      {greska && <div style={upozorenje}><AlertTriangle size={14} /> {greska}</div>}
      {poruka && <div style={obavestenje}>{poruka}</div>}
      {ucitavanje && <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Učitavanje…</p>}

      {prazna.length > 0 && (
        <div style={{ ...upozorenje, alignItems: 'flex-start', flexDirection: 'column', gap: 6 }}>
          <strong style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={14} /> Nedostaje {prazna.length} podatak/podataka
          </strong>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {prazna.map((n) => (
              <li key={n.polje}><strong>{n.polje}</strong> - {n.posledica}</li>
            ))}
          </ul>
        </div>
      )}

      <section style={kartica}>
        <h3 style={naslov}><Building2 size={16} /> Identitet</h3>
        <p style={opis}>Zaglavlje ugovora, izdavalac na uplatnici, podnožje PDF-a.</p>

        <label style={oznaka}>Poslovno ime</label>
        <input {...polja('companyName')} placeholder="npr. Efikasa d.o.o." />

        <div style={dvaStupca}>
          <div>
            <label style={oznaka}>PIB</label>
            <input {...polja('taxId')} placeholder="9 cifara" inputMode="numeric" />
          </div>
          <div>
            <label style={oznaka}>Matični broj</label>
            <input {...polja('registrationNumber')} placeholder="8 cifara" inputMode="numeric" />
          </div>
        </div>

        <div style={dvaStupca}>
          <div>
            <label style={oznaka}>Adresa</label>
            <input {...polja('address')} placeholder="ulica i broj" />
          </div>
          <div>
            <label style={oznaka}>Grad</label>
            <input {...polja('city')} placeholder="npr. 11000 Beograd" />
          </div>
        </div>
      </section>

      <section style={kartica}>
        <h3 style={naslov}><Landmark size={16} /> Uplatnica</h3>
        <p style={opis}>
          Primalac na uplatnici uz račun za pretplatu. Bez računa klijent nema gde da uplati.
        </p>

        <div style={dvaStupca}>
          <div>
            <label style={oznaka}>Tekući račun</label>
            <input {...polja('bankAccount')} placeholder="160-0000000000000-00" />
          </div>
          <div>
            <label style={oznaka}>Banka</label>
            <input {...polja('bankName')} placeholder="npr. Banca Intesa" />
          </div>
        </div>

        <label style={oznaka}>Šifra plaćanja</label>
        <input {...polja('paymentCode')} placeholder="221" inputMode="numeric" style={{ ...polje, maxWidth: 140 }} />
        <p style={{ ...opis, marginTop: 4 }}>221 = promet robe i usluga.</p>
      </section>

      <section style={kartica}>
        <h3 style={naslov}><FileSignature size={16} /> Potpisnik i kontakt</h3>
        <p style={opis}>
          Potpisni blok ugovora i kontakt na koji politika privatnosti upućuje za zahteve lica
          čiji se podaci obrađuju.
        </p>

        <div style={dvaStupca}>
          <div>
            <label style={oznaka}>Ime potpisnika</label>
            <input {...polja('directorName')} placeholder="Ime i prezime" />
          </div>
          <div>
            <label style={oznaka}>Funkcija</label>
            <input {...polja('directorTitle')} placeholder="Direktor" />
          </div>
        </div>

        <div style={dvaStupca}>
          <div>
            <label style={oznaka}>Kontakt e-pošta</label>
            <input {...polja('contactEmail')} placeholder="office@primer.rs" />
          </div>
          <div>
            <label style={oznaka}>Internet stranica</label>
            <input {...polja('website')} placeholder="www.primer.rs" />
          </div>
        </div>
      </section>

      {upozorenja.length > 0 && (
        <div style={{ ...obavestenjeBlago, display: 'grid', gap: 4 }}>
          {upozorenja.map((u) => <span key={u}>⚠ {u}</span>)}
          <span style={{ opacity: 0.75 }}>
            Čuvanje i dalje prolazi - zatečen podatak može biti stariji od pravila.
          </span>
        </div>
      )}

      <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted)' }}>
        Izmena važi za <strong>nove</strong> ugovore i uplatnice. Već izdat ugovor nosi podatke iz
        trenutka izdavanja i ne menja se (ADR-043).
      </p>
    </div>
  );
}

const kartica: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 10, padding: 20,
  background: 'var(--color-bg-surface, #fff)',
};

const naslov: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)',
};

const opis: React.CSSProperties = {
  margin: '0 0 12px', fontSize: 12, color: 'var(--color-text-muted)',
};

const dvaStupca: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 12,
};

const oznaka: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, margin: '10px 0 4px',
  color: 'var(--color-text-strong)',
};

const polje: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13,
  border: '1px solid var(--color-border)', background: 'var(--color-bg-surface, #fff)',
  color: 'var(--color-text-strong)', boxSizing: 'border-box',
};

const btnGhost: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text-muted)',
};

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
  border: 'none', background: 'var(--color-primary, #2563eb)', color: '#fff', fontWeight: 600,
};

const upozorenje: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '10px 12px', borderRadius: 6,
  background: 'rgba(220,38,38,0.08)', color: '#dc2626', fontSize: 13,
};

const obavestenje: React.CSSProperties = {
  padding: '10px 12px', borderRadius: 6,
  background: 'rgba(22,163,74,0.10)', color: '#15803d', fontSize: 13,
};

const obavestenjeBlago: React.CSSProperties = {
  padding: '10px 12px', borderRadius: 6,
  background: 'rgba(217,119,6,0.10)', color: '#b45309', fontSize: 12,
};
